import { motion } from 'framer-motion';
import {
  Edit3,
  Save,
  Star,
  Target,
  TrendingUp,
  Trophy,
  User,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';

interface UserProfile {
  id: string;
  name: string;
  bio: string;
  avatar_url: string;
  username?: string;
  preferences: any;
  created_at: string;
  updated_at: string;
}

interface UserStats {
  completed: number;
  attempted: number;
  completionRate: number;
  currentStreak: number;
}

const Profile: React.FC = () => {
  const { user, getAuthToken, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    name: '',
    bio: '',
    avatar_url: '',
    username: '',
  });

  const fetchProfile = useCallback(async () => {
    setFetchError(null);
    try {
      const token = await getAuthToken();
      if (!token) {
        setProfile(null);
        setLoading(false);
        return;
      }
      const response = await fetch(`${getApiUrl()}/api/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          setProfile(null);
        }
        setLoading(false);
        return;
      }
      const data = await response.json();
      setProfile(data.user.profile);
      setEditForm({
        name: data.user.profile?.name || '',
        bio: data.user.profile?.bio || '',
        avatar_url: data.user.profile?.avatar_url || '',
        username: data.user.profile?.username || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setFetchError(
        'Unable to load profile. Check that the API is running and try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  const fetchStats = useCallback(async () => {
    try {
      if (!user?.id) return;
      const token = await getAuthToken();
      const response = await fetch(`${getApiUrl()}/api/users/${user?.id}/stats`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        setStats(null);
      } else {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setFetchError(prev => prev || 'Unable to load stats.');
    } finally {
      setLoading(false);
    }
  }, [user?.id, getAuthToken]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    fetchProfile();
    fetchStats();
  }, [authLoading, user, fetchProfile, fetchStats]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate user is logged in
      if (!user) {
        alert('You must be logged in to update your profile');
        setSaving(false);
        return;
      }

      const token = await getAuthToken();

      // Validate token exists and is a valid JWT format
      if (!token || typeof token !== 'string' || token.trim() === '') {
        console.error('No authentication token available');
        alert('Your session has expired. Please log in again.');
        setSaving(false);
        return;
      }

      // Basic JWT format validation (should have 3 parts separated by dots)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('Invalid token format:', token.substring(0, 20) + '...');
        alert('Your session token is invalid. Please log in again.');
        setSaving(false);
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editForm.name,
          bio: editForm.bio,
          avatar_url: editForm.avatar_url,
          username: editForm.username || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Profile update response:', data); // Debug log
        setProfile(data.profile);
        setEditing(false);
        // Refresh profile from server to ensure we have the latest data
        await fetchProfile();
      } else {
        let errorMessage = 'Unknown error';
        try {
          const errorData = await response.json();
          console.error(
            'Error updating profile:',
            JSON.stringify(errorData, null, 2)
          );

          // Extract error message from various possible formats
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else {
            errorMessage = `Server error: ${response.status} ${response.statusText}`;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          const text = await response.text().catch(() => '');
          console.error('Raw error response:', text);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        alert(`Failed to update profile: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('An error occurred while updating your profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm({
      name: profile?.name || '',
      bio: profile?.bio || '',
      avatar_url: profile?.avatar_url || '',
      username: profile?.username || '',
    });
    setEditing(false);
  };

  if (loading && !profile) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-hexagon to-hexagon-dark animate-pulse'></div>
          </div>
          <p className='text-gray-600'>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (fetchError && !profile) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center max-w-md'>
          <p className='text-gray-600 mb-4'>{fetchError}</p>
          <p className='text-sm text-gray-500 mb-6'>
            Make sure the API server is running: <code className='bg-gray-100 px-2 py-1 rounded'>cd api && npm run dev</code>
          </p>
          <button
            onClick={() => {
              setLoading(true);
              setFetchError(null);
              fetchProfile();
              fetchStats();
            }}
            className='hexagon-button px-6 py-3'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-8'>
      {/* Error banner (e.g. stats failed but profile loaded) */}
      {fetchError && (
        <div className='rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-center justify-between gap-4'>
          <p className='text-amber-800 text-sm'>{fetchError}</p>
          <button
            onClick={() => {
              setFetchError(null);
              fetchProfile();
              fetchStats();
            }}
            className='hexagon-button px-4 py-2 text-sm shrink-0'
          >
            Retry
          </button>
        </div>
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className='text-center'
      >
        <h1 className='text-3xl lg:text-4xl font-bold text-gray-900 mb-4'>
          Your Profile
        </h1>
        <p className='text-xl text-gray-600'>
          Manage your account and track your learning progress
        </p>
      </motion.div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
        {/* Profile Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className='lg:col-span-2 space-y-6'
        >
          {/* Profile Card */}
          <div className='glass-effect rounded-2xl p-8'>
            <div className='flex items-start justify-between mb-6'>
              <div className='flex items-center space-x-6'>
                <div className='w-24 h-24'>
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt='Profile'
                      className='w-24 h-24 rounded-full object-cover'
                    />
                  ) : (
                    <div className='hexagon bg-gradient-to-r from-hexagon to-accent-500 flex items-center justify-center w-24 h-24'>
                      <User className='w-12 h-12 text-white' />
                    </div>
                  )}
                </div>

                <div>
                  <h2 className='text-2xl font-bold text-gray-900 mb-2'>
                    {editing ? (
                      <input
                        type='text'
                        value={editForm.name}
                        onChange={e =>
                          setEditForm({ ...editForm, name: e.target.value })
                        }
                        className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent'
                        placeholder='Enter your name'
                      />
                    ) : (
                      profile?.name || 'No name set yet'
                    )}
                  </h2>

                  <p className='text-gray-600 mb-2'>{user?.email}</p>
                  {profile?.username && (
                    <p className='text-sm text-gray-500 mb-2'>
                      Username: {profile.username}
                    </p>
                  )}

                  <p className='text-sm text-gray-500'>
                    Member since{' '}
                    {profile?.created_at
                      ? new Date(profile.created_at).toLocaleDateString()
                      : 'Recently'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setEditing(!editing)}
                className='inline-flex items-center space-x-2 px-4 py-2 bg-hexagon text-white rounded-lg hover:bg-hexagon-dark transition-colors duration-200'
              >
                {editing ? (
                  <>
                    <X className='w-4 h-4' />
                    <span>Cancel</span>
                  </>
                ) : (
                  <>
                    <Edit3 className='w-4 h-4' />
                    <span>Edit</span>
                  </>
                )}
              </button>
            </div>

            {/* Username Section */}
            {editing && (
              <div className='mb-6'>
                <h3 className='text-lg font-semibold text-gray-900 mb-3'>
                  Username
                </h3>
                <input
                  type='text'
                  value={editForm.username}
                  onChange={e =>
                    setEditForm({ ...editForm, username: e.target.value })
                  }
                  className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent'
                  placeholder='Username for login (optional)'
                />
                <p className='text-sm text-gray-500 mt-1'>
                  Set a username to sign in with username instead of email.
                </p>
              </div>
            )}

            {/* Bio Section */}
            <div className='mb-6'>
              <h3 className='text-lg font-semibold text-gray-900 mb-3'>
                About
              </h3>
              {editing ? (
                <textarea
                  value={editForm.bio}
                  onChange={e =>
                    setEditForm({ ...editForm, bio: e.target.value })
                  }
                  className='w-full h-32 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent resize-none'
                  placeholder='Tell us about yourself...'
                />
              ) : (
                <p className='text-gray-600'>
                  {profile?.bio || 'No bio added yet. Click edit to add one!'}
                </p>
              )}
            </div>

            {/* Avatar URL Section */}
            {editing && (
              <div className='mb-6'>
                <h3 className='text-lg font-semibold text-gray-900 mb-3'>
                  Profile Picture URL
                </h3>
                <input
                  type='url'
                  value={editForm.avatar_url}
                  onChange={e =>
                    setEditForm({ ...editForm, avatar_url: e.target.value })
                  }
                  className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent'
                  placeholder='https://example.com/avatar.jpg'
                />
              </div>
            )}

            {/* Save Button */}
            {editing && (
              <div className='flex justify-end space-x-4'>
                <button
                  onClick={handleCancel}
                  className='px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200'
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className='hexagon-button inline-flex items-center space-x-2 disabled:opacity-50'
                >
                  <Save className='w-4 h-4' />
                  <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Learning Achievements */}
          <div className='glass-effect rounded-2xl p-8'>
            <h3 className='text-xl font-semibold text-gray-900 mb-6'>
              Learning Achievements
            </h3>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div className='text-center p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl'>
                <div className='w-16 h-16 mx-auto mb-4'>
                  <div className='hexagon bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center'>
                    <Trophy className='w-8 h-8 text-white' />
                  </div>
                </div>
                <div className='text-2xl font-bold text-yellow-800'>
                  {stats?.completed || 0}
                </div>
                <div className='text-yellow-700'>Exercises Completed</div>
              </div>

              <div className='text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl'>
                <div className='w-16 h-16 mx-auto mb-4'>
                  <div className='hexagon bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center'>
                    <Target className='w-8 h-8 text-white' />
                  </div>
                </div>
                <div className='text-2xl font-bold text-blue-800'>
                  {stats?.currentStreak || 0}
                </div>
                <div className='text-blue-700'>Day Streak</div>
              </div>

              <div className='text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl'>
                <div className='w-16 h-16 mx-auto mb-4'>
                  <div className='hexagon bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center'>
                    <TrendingUp className='w-8 h-8 text-white' />
                  </div>
                </div>
                <div className='text-2xl font-bold text-green-800'>
                  {stats?.completionRate || 0}%
                </div>
                <div className='text-green-700'>Completion Rate</div>
              </div>

              <div className='text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl'>
                <div className='w-16 h-16 mx-auto mb-4'>
                  <div className='hexagon bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center'>
                    <Star className='w-8 h-8 text-white' />
                  </div>
                </div>
                <div className='text-2xl font-bold text-purple-800'>
                  {stats?.attempted || 0}
                </div>
                <div className='text-purple-700'>Exercises Attempted</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className='space-y-6'
        >
          {/* Quick Stats */}
          <div className='glass-effect rounded-2xl p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Quick Stats
            </h3>

            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-gray-600'>Total Exercises</span>
                <span className='font-medium'>{stats?.attempted || 0}</span>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-gray-600'>Completed</span>
                <span className='font-medium text-green-600'>
                  {stats?.completed || 0}
                </span>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-gray-600'>In Progress</span>
                <span className='font-medium text-blue-600'>
                  {(stats?.attempted || 0) - (stats?.completed || 0)}
                </span>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-gray-600'>Success Rate</span>
                <span className='font-medium text-purple-600'>
                  {stats?.completionRate || 0}%
                </span>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className='glass-effect rounded-2xl p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Recent Activity
            </h3>

            <div className='space-y-3'>
              <div className='flex items-center space-x-3 text-sm'>
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                <span className='text-gray-600'>Profile updated</span>
                <span className='text-gray-400 text-xs'>Today</span>
              </div>

              <div className='flex items-center space-x-3 text-sm'>
                <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                <span className='text-gray-600'>Exercise completed</span>
                <span className='text-gray-400 text-xs'>2 days ago</span>
              </div>

              <div className='flex items-center space-x-3 text-sm'>
                <div className='w-2 h-2 bg-purple-500 rounded-full'></div>
                <span className='text-gray-600'>New exercise started</span>
                <span className='text-gray-400 text-xs'>1 week ago</span>
              </div>
            </div>
          </div>

          {/* Profile Completion */}
          <div className='glass-effect rounded-2xl p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Profile Completion
            </h3>

            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Name</span>
                <div
                  className={`w-3 h-3 rounded-full ${
                    profile?.name ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                ></div>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Bio</span>
                <div
                  className={`w-3 h-3 rounded-full ${
                    profile?.bio ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                ></div>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Avatar</span>
                <div
                  className={`w-3 h-3 rounded-full ${
                    profile?.avatar_url ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                ></div>
              </div>
            </div>

            <div className='mt-4 pt-4 border-t border-gray-200'>
              <div className='flex items-center justify-between mb-2'>
                <span className='text-sm font-medium text-gray-700'>
                  Overall
                </span>
                <span className='text-sm text-gray-500'>
                  {[
                    profile?.name ? 1 : 0,
                    profile?.bio ? 1 : 0,
                    profile?.avatar_url ? 1 : 0,
                  ].reduce((a, b) => a + b, 0) * 33}
                  %
                </span>
              </div>
              <div className='w-full bg-gray-200 rounded-full h-2'>
                <div
                  className='bg-gradient-to-r from-hexagon to-accent-500 h-2 rounded-full transition-all duration-300'
                  style={{
                    width: `${Math.max(
                      0,
                      Math.min(
                        100,
                        [
                          profile?.name ? 1 : 0,
                          profile?.bio ? 1 : 0,
                          profile?.avatar_url ? 1 : 0,
                        ].reduce((a, b) => a + b, 0) * 33
                      )
                    )}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Profile;
